import { Box } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React from 'react';
import { useDisponibilidadPorPasos } from '../../../../hooks/useDisponibilidadPorPasos';
import { usePasosObjetivo } from '../../../../hooks/usePasosObjetivo';
import { BarChart } from '../../../../components/Charts/BarChart';
import { useDynamicViewState } from '../../context/dynamic-view-context';
import { AtCircularProgress } from '../../../../components/AtCircularProgress';
import { useTranslation } from 'react-i18next';
import at_codes_ok from '../../../../data/codes_ok';

/**
 * Parsea la duración de un evento en segundos.
 * @param {string} duracion - Duración en formato string
 * @returns {number} - Duración en segundos
 */
function parseDuration(duracion) {
  const parts = String(duracion).split(' ');
  if (parts.length === 1) {
    const [hours, minutes, seconds] = parts[0].split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  } else {
    // Formato "X days HH:mm:ss"
    const days = parseInt(parts[0], 10);
    const [hours, minutes, seconds] = parts[2].split(':').map(Number);
    return days * 86400 + hours * 3600 + minutes * 60 + seconds;
  }
}

/**
 * Procesa los datos de eventos para calcular disponibilidad por paso.
 * @param {Array} externalData - Datos de eventos desde la API
 * @param {Array} pasos - Lista de pasos del objetivo
 * @returns {Object} - Datos procesados para el gráfico
 */
function processEventsData(externalData, pasos) {
  const atCodesOkIds = at_codes_ok.map((i) => i.id);
  const chartCategory = [];
  const chartSerieUp = [];
  const chartSerieDown = [];
  const chartSerieNoMon = [];

  if (!pasos || !pasos.length || !externalData || !externalData.length) {
    return { chartCategory, chartSerieUp, chartSerieDown, chartSerieNoMon };
  }

  pasos.forEach((step) => {
    chartCategory.push(step.nombre_paso);
    let contUpDuration = 0;
    let contDownDuration = 0;
    let contNoMonDuration = 0;

    externalData.forEach((nodeData) => {
      if (!nodeData.data_nodo) return;

      nodeData.data_nodo.forEach((dataEvents) => {
        dataEvents.forEach((event) => {
          const stateEvent = event.pasos?.find((i) => i.id === step.id_paso);
          if (!stateEvent) return;

          const duration = parseDuration(event.duracion);
          let estado = 0;

          if (stateEvent.patrones) {
            let contPattern = 0;
            let contPatternDown = 0;
            let contPatternNoMon = 0;

            stateEvent.patrones.forEach((patron) => {
              if (atCodesOkIds.includes(parseInt(patron.estado, 10))) {
                contPattern += 1;
              } else if (patron.estado === 'sin monitoreo') {
                contPatternNoMon += 1;
              } else {
                contPatternDown += 1;
              }
            });

            if (contPattern > 0) {
              estado = 0;
            } else if (contPatternDown > 0) {
              estado = 3;
            } else if (
              contPatternNoMon > 0 &&
              contPattern === 0 &&
              contPatternDown === 0
            ) {
              estado = 5;
            }
          } else {
            if (atCodesOkIds.includes(parseInt(stateEvent.estado, 10))) {
              estado = 0;
            } else if (stateEvent.estado === 'sin monitoreo') {
              estado = 5;
            } else {
              estado = 3;
            }
          }

          if (estado === 0) {
            contUpDuration += duration;
          } else if (estado === 5) {
            contNoMonDuration += duration;
          } else {
            contDownDuration += duration;
          }
        });
      });
    });

    // Calcular porcentajes
    // Nota: Se establece contNoMonDuration en 0 como en el componente original
    contNoMonDuration = 0;
    const total = contUpDuration + contNoMonDuration + contDownDuration;

    if (total > 0) {
      chartSerieUp.push(
        parseFloat(((contUpDuration * 100) / total).toFixed(2)),
      );
      chartSerieDown.push(
        parseFloat(((contDownDuration * 100) / total).toFixed(2)),
      );
      chartSerieNoMon.push(
        parseFloat(((contNoMonDuration * 100) / total).toFixed(2)),
      );
    } else {
      chartSerieUp.push(0);
      chartSerieDown.push(0);
      chartSerieNoMon.push(0);
    }
  });

  return { chartCategory, chartSerieUp, chartSerieDown, chartSerieNoMon };
}

/**
 * Componente que muestra la distribución porcentual de disponibilidad por paso.
 * Basado en la lógica del componente AvailabilityNodeDetail pero adaptado
 * para el Panel Dinámico siguiendo la estructura de AvailabilityIsp.
 *
 * @param {Object} props - Propiedades del componente
 * @param {number} props.objetivoId - ID del objetivo a consultar
 * @returns {React.ReactElement} Gráfico de barras con disponibilidad por paso
 *
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export function ConsolidatedAvailabilityBySteps({ objetivoId }) {
  const { filterPeriod, refreshTime } = useDynamicViewState();
  const { t } = useTranslation();

  // Obtener los pasos del objetivo
  const {
    data: pasosData,
    isLoading: isLoadingPasos,
    isError: isErrorPasos,
  } = usePasosObjetivo({ objetivoId });

  // Transformar los datos de pasos al formato esperado
  const pasos = React.useMemo(() => {
    if (!pasosData || !Array.isArray(pasosData)) return [];
    return pasosData.map((paso) => ({
      id_paso: paso.paso_orden,
      nombre_paso: paso.paso_nombre,
    }));
  }, [pasosData]);

  // Obtener datos de eventos desde la API
  const {
    data: eventsData,
    isLoading: isLoadingEvents,
    isFetching,
    isError: isErrorEvents,
  } = useDisponibilidadPorPasos({
    objetivoId,
    time: filterPeriod.period,
    statePeriod: filterPeriod.statePeriod,
    enabled: pasos.length > 0,
    refreshTime,
  });

  // Procesar los datos para el gráfico
  const processedData = React.useMemo(() => {
    if (!eventsData || !pasos.length) {
      return {
        chartSerieUp: [],
        chartSerieDown: [],
        chartSerieNoMon: [],
        chartCategory: [],
      };
    }
    return processEventsData(eventsData, pasos);
  }, [eventsData, pasos]);

  const isLoading = isLoadingPasos || isLoadingEvents || isFetching;
  const isError = isErrorPasos || isErrorEvents;

  // Manejo de estados de carga y error
  if (isLoading) {
    return <AtCircularProgress />;
  }

  if (isError) {
    return (
      <Alert severity="error">
        {t('availability-percentage-by-step.error.load-data')}
      </Alert>
    );
  }

  if (!processedData.chartCategory?.length) {
    return (
      <Alert severity="warning">
        {t('availability-percentage-by-step.error.no-data')}
      </Alert>
    );
  }

  const {
    chartSerieUp,
    chartSerieDown,
    chartSerieNoMon,
    chartCategory,
  } = processedData;

  return (
    <Box>
      <BarChart
        chartCategory={chartCategory}
        chartSerieUp={chartSerieUp}
        chartSerieDown={chartSerieDown}
        chartSerieNoMon={chartSerieNoMon}
        title={t('availability-percentage-by-step.chart.title')}
        isLoading={isLoading}
      />
    </Box>
  );
}
